import React from 'react';
import { SetterOrUpdater } from 'recoil';
import { IUserAtom } from '../../recoil/atoms/user';
import { Logger } from '../../common/Logger';
import { StatusCodes } from 'http-status-codes';
import { refreshTokenAjax } from '../auth/refreshTokenAjax';
import cookie from 'react-cookies';
import Axios, {
  AxiosError,
  AxiosRequestConfig,
  CancelTokenSource,
  Method,
} from 'axios';
import axiosInstance from '../../common/axiosInterceptor';

/**
 * Get function that performs ajax call via axios to be used with useMutation
 *
 * @param userAtom Contens of user atom
 * @param setUserAtom Setter for user atom
 * @param [setCancelToken] Optional setter for cancel token used by caller to cancel an in-flight query
 *
 * @example
 *  import { useMutation } from 'react-query';
 *  import { getApiMutation } from '../../services/rest/getApiMutation';
 *  import { IUserAtom, user } from '../../recoil/atoms/user';
 *  const [cancelToken, setCancelToken] = useState<CancelTokenSource | undefined>();
 *  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<IUserAtom | Record<string, unknown>>(user);
 *  const userAtom = userAtomLoadable.contents as IUserAtom;
 *  const mutation = useMutation(getApiMutation(userAtom, setUserAtom, setCancelToken));
 *  try {
 *    const response = await mutation.mutateAsync({
 *      endpoint: `${endpointEnum.DELETE_CADENCE}/${id}`,
 *      method: 'delete',
 *      omitBusinessIdAndApikey: true,
 *    });
 *  } catch (exception) {
 *    console.error('failed: ajax query');
 *  }
 *  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => { cancelToken?.cancel(); }
 */
export const getApiMutation = (
  userAtom: IUserAtom,
  setUserAtom: SetterOrUpdater<IUserAtom | Record<string, unknown>>,
  setCancelToken?: React.Dispatch<
    React.SetStateAction<CancelTokenSource | undefined>
  >
) => async (request: {
  /** Name of endpoint on API (eg. /getnumbereport)  */
  endpoint: string;
  /** Payload to send to API server */
  payload?: { [key: string]: any }; // indexable object
  /** HTTP method to call */
  method?: Method;
  /** wheather to include businessId and apikey in payload */
  omitBusinessIdAndApikey?: boolean;
  domain?: string;
  stage?: 'itn' | 'app' | 'auto' | 'crm' | 'utils' | 'tele';
}) => {
  const accessToken = cookie.load('accessToken');
  const cancelTokenSource = Axios.CancelToken.source();
  const safeSetCancelToken = (cancelToken: CancelTokenSource | undefined) => {
    if (setCancelToken) {
      setCancelToken(cancelToken);
    }
  };

  // execute loop twice
  // for (let retries = 0; retries < 2; retries++) {}
  const config: AxiosRequestConfig = {
    method: request.method ?? 'post',
    url: `${request?.domain ?? process.env.VITE_APP_API_DOMAIN}/${
      request?.stage ?? process.env.VITE_APP_API_STAGE
    }/${request.endpoint}`,
    data: request.omitBusinessIdAndApikey
      ? request.payload
      : {
          businessid: userAtom.businessid,
          apikey: userAtom.apikey,
          apiKey: userAtom.apikey,
          ...request.payload,
        },
    headers: {
      Authorization: accessToken, // accessToken in recoil maybe stale
      'X-Api-Key': userAtom.apikey,
      'Content-Type': 'application/json;charset=utf-8',
    },
    cancelToken: cancelTokenSource.token,
  };

  const logData = {
    pathname: window.location.pathname,
    businessid: userAtom.businessid,
    apiKey: userAtom.apikey,
    userid: userAtom.user?.userid,
    endpoint: request.endpoint,
    payload: request.payload,
    config,
  };

  try {
    safeSetCancelToken(cancelTokenSource);
    const response = await axiosInstance(config);
    safeSetCancelToken(undefined);
    Logger.info(
      {
        ...logData,
        // response, // too large to print
        message: 'success: ajax request',
      },
      'getApiMutation'
    );
    return response;
  } catch (exception) {
    const axiosError = exception as AxiosError;
    const statusCode: StatusCodes | undefined = axiosError.response?.status;

    // retry logic on cors error only when setUserAtom is passed
    if (statusCode === undefined) {
      const refresher = await refreshTokenAjax({ setUserAtom });
      if (refresher.success) {
        const newUserAtom = refresher.loginUser as IUserAtom;
        const newAccessToken = newUserAtom.accessToken; // get refreshed access token

        // log error
        Logger.error(
          {
            ...logData,
            message: 'CORS error',
          },
          'getApiMutation'
        );

        try {
          const response = await axiosInstance({
            ...config,
            headers: {
              Authorization: newAccessToken, // accessToken in recoil maybe stale
              'X-Api-Key': newUserAtom.apikey,
              'Content-Type': 'application/json;charset=utf-8',
            },
          });

          Logger.info(
            {
              ...logData,
              // response, // too large to print
              message: 'success: ajax request',
            },
            'getApiMutation'
          );

          return response;
        } catch (innerException) {
          Logger.error(
            {
              ...logData,
              exception,
              message: 'failed: ajax request',
            },
            'getApiMutation'
          );

          throw innerException;
        }
      }
    }

    Logger.error(
      {
        ...logData,
        exception,
        message: 'failed: ajax request',
      },
      'getApiMutation'
    );

    throw exception;
  }
};
